import { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Switch,
  Collapse,
  Link,
  Stack,
  FormControlLabel,
} from '@mui/material';
import { Cookie } from '@mui/icons-material';
import { getConsent, saveConsent } from '../../hooks/useCookieConsent';

export function CookieConsentBanner() {
  const [visible, setVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [functional, setFunctional] = useState(false);
  const [analytics, setAnalytics] = useState(false);

  useEffect(() => {
    const consent = getConsent();
    if (!consent) {
      setVisible(true);
    } else {
      setFunctional(consent.functional);
      setAnalytics(consent.analytics);
    }
  }, []);

  const handleAcceptAll = () => {
    saveConsent({ functional: true, analytics: true });
    setVisible(false);
  };

  const handleRejectAll = () => {
    saveConsent({ functional: false, analytics: false });
    setVisible(false);
  };

  const handleSavePreferences = () => {
    saveConsent({ functional, analytics });
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <Paper
      elevation={6}
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1400,
        p: { xs: 2, sm: 3 },
        borderRadius: 0,
        borderTop: 1,
        borderColor: 'divider',
      }}
    >
      <Box sx={{ maxWidth: 960, mx: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1 }}>
          <Cookie sx={{ color: 'primary.main', mt: 0.25 }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" fontWeight={500}>
              Cookie Preferences
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              We use cookies to ensure the app functions properly. Some cookies are
              essential, while others help us improve your experience. See our{' '}
              <Link component={RouterLink} to="/privacy">
                Privacy Policy
              </Link>{' '}
              for details.
            </Typography>
          </Box>
        </Box>

        <Collapse in={showPreferences}>
          <Box sx={{ ml: { sm: 5 }, mt: 2, mb: 1 }}>
            <FormControlLabel
              control={<Switch checked disabled />}
              label={
                <Box>
                  <Typography variant="body2" fontWeight={500}>
                    Essential
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Authentication and security. Always active.
                  </Typography>
                </Box>
              }
              sx={{ mb: 1, alignItems: 'flex-start' }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={functional}
                  onChange={(e) => setFunctional(e.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" fontWeight={500}>
                    Functional
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Theme preferences and UI settings.
                  </Typography>
                </Box>
              }
              sx={{ mb: 1, alignItems: 'flex-start' }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={analytics}
                  onChange={(e) => setAnalytics(e.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" fontWeight={500}>
                    Analytics
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Usage data to help us improve the app.
                  </Typography>
                </Box>
              }
              sx={{ alignItems: 'flex-start' }}
            />
          </Box>
        </Collapse>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          sx={{ mt: 2, ml: { sm: 5 }, justifyContent: 'flex-start' }}
        >
          {showPreferences ? (
            <Button variant="contained" onClick={handleSavePreferences}>
              Save Preferences
            </Button>
          ) : (
            <>
              <Button variant="contained" onClick={handleAcceptAll}>
                Accept All
              </Button>
              <Button variant="outlined" onClick={handleRejectAll}>
                Reject All
              </Button>
            </>
          )}
          <Button
            variant="text"
            onClick={() => setShowPreferences(!showPreferences)}
          >
            {showPreferences ? 'Hide Preferences' : 'Manage Preferences'}
          </Button>
        </Stack>
      </Box>
    </Paper>
  );
}
